import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, ClipboardList } from "lucide-react";
import {
    EMPTY_ISSUE_FILTERS, filterIssues, IssueFilters, IssueTable, IssueFormModal, exportIssuesPDF, exportIssuesXLSX,
} from "@/components/IssuesTab";

export default function Issues() {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ ...EMPTY_ISSUE_FILTERS });
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [collapsed, setCollapsed] = useState(new Set());

    async function reload() {
        setLoading(true);
        try {
            const r = await api.get("/issues");
            setIssues(r.data);
        } finally { setLoading(false); }
    }
    useEffect(() => { reload(); }, []);

    async function setStatus(i, status) {
        await api.patch(`/issues/${i.id}`, { status });
        await reload();
    }
    async function remove(i) {
        if (!window.confirm(`Excluir a ${i.tipo} "${i.titulo}"?\nEsta ação não pode ser desfeita.`)) return;
        await api.delete(`/issues/${i.id}`);
        await reload();
    }

    const resumo = useMemo(() => ({
        total: issues.length,
        pendentes: issues.filter(i => i.status === "pendente").length,
        em_andamento: issues.filter(i => i.status === "em_andamento").length,
        concluidas: issues.filter(i => i.status === "concluida").length,
    }), [issues]);

    const filtered = useMemo(() => filterIssues(issues, filters), [issues, filters]);

    const groups = useMemo(() => {
        const g = {};
        filtered.forEach(i => {
            const key = i.contract_id;
            (g[key] = g[key] || { contract_id: key, organizacao_nome: i.organizacao_nome, items: [] }).items.push(i);
        });
        return Object.values(g).sort((a, b) => a.organizacao_nome.localeCompare(b.organizacao_nome));
    }, [filtered]);

    function toggleGroup(cid) {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(cid)) next.delete(cid); else next.add(cid);
            return next;
        });
    }

    return (
        <div className="space-y-6 fade-in" data-testid="issues-page">
            <div>
                <h1 className="font-display text-2xl font-semibold text-[#0F2044]">Issues / Tasks</h1>
                <p className="text-sm text-slate-500 mt-1">Visão consolidada de todas as issues e tasks de todos os contratos, agrupadas por cliente.</p>
            </div>

            {/* Resumo consolidado */}
            <section className="grid grid-cols-4 gap-4" data-testid="issues-summary">
                <SummaryCard label="Total" value={resumo.total} color="#0F2044" testid="issues-total" />
                <SummaryCard label="Pendentes" value={resumo.pendentes} color="#B91C1C" testid="issues-pendentes" />
                <SummaryCard label="Em andamento" value={resumo.em_andamento} color="#1D4ED8" testid="issues-andamento" />
                <SummaryCard label="Concluídas" value={resumo.concluidas} color="#047857" testid="issues-concluidas-count" />
            </section>

            <IssueFilters filters={filters} onChange={setFilters} onNew={() => { setEditing(null); setFormOpen(true); }}
                onExport={() => exportIssuesPDF(filtered, { title: "Issues / Tasks — Todos os clientes", showCliente: true })}
                onExportXlsx={() => exportIssuesXLSX(filtered, { title: "Issues / Tasks — Todos os clientes" })}
                testidPrefix="issues-page" />

            {loading ? (
                <div className="text-sm text-slate-500 py-10 text-center">Carregando…</div>
            ) : groups.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500" data-testid="issues-empty">
                    <ClipboardList className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    Nenhuma issue/task encontrada. Cadastre a primeira pelo botão "Nova issue/task".
                </div>
            ) : (
                <div className="space-y-4">
                    {groups.map(g => {
                        const isCollapsed = collapsed.has(g.contract_id);
                        const abertas = g.items.filter(i => i.status !== "concluida").length;
                        return (
                            <div key={g.contract_id} className="bg-white border border-slate-200 rounded-lg" data-testid={`issues-group-${g.contract_id}`}>
                                <button onClick={() => toggleGroup(g.contract_id)} data-testid={`issues-group-toggle-${g.contract_id}`}
                                    className="w-full flex items-center gap-2 px-6 py-4 text-left hover:bg-slate-50 rounded-t-lg">
                                    {isCollapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                                    <span className="font-display font-semibold text-[#0F2044]">{g.organizacao_nome}</span>
                                    <span className="text-xs text-slate-500">{g.items.length} registro(s) · {abertas} em andamento</span>
                                    <Link to={`/contratos/${g.contract_id}`} onClick={(e) => e.stopPropagation()}
                                        data-testid={`issues-group-open-${g.contract_id}`}
                                        className="ml-auto text-xs font-semibold text-[#0F2044] hover:underline">
                                        Abrir contrato
                                    </Link>
                                </button>
                                {!isCollapsed && (
                                    <div className="px-6 pb-5">
                                        <IssueTable issues={g.items} onEdit={(i) => { setEditing(i); setFormOpen(true); }}
                                            onDelete={remove} onStatusChange={setStatus} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {formOpen && (
                <IssueFormModal issue={editing}
                    onClose={() => setFormOpen(false)}
                    onSaved={async () => { setFormOpen(false); setEditing(null); await reload(); }} />
            )}
        </div>
    );
}

function SummaryCard({ label, value, color, testid }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
            <div className="mt-1.5 text-3xl font-display font-bold" style={{ color }}>{value}</div>
        </div>
    );
}
